import { Local697FormOfPayment } from "app/calcs/customers/local697/interfaces"
import { SimplePensionFormOfPayment } from "app/calcs/pensions/interfaces"
import { getDefaultRetirementSavingsPersonInputs } from "app/calcs/retirementSavings"
import { Profile, updateLifeExpectancy } from "app/redux/Profile/model"
import { Gender } from "app/redux/Profile/model/interfaces"
import { parseYesNo } from "app/utils"
import { ColumnNames } from "config/697/ETL/interfaces"
import * as _ from "lodash"
import * as moment from "moment"

const genderMap = {
    M: Gender.male,
    F: Gender.female,
}
const gm = (g: string) => genderMap[g] || Gender.unidentified

/**
 * Populates a profile with the information found in 'extraInfo'.
 *
 * @param profile - the profile to load.
 */
function loadProfilePresets(profile: Profile): Promise<Profile> {
    const ex = profile.extraInfo

    const member = profile.getHeadOfHousehold()
    member.firstName = ex[ColumnNames.memberFirstName]
    member.lastName = ex[ColumnNames.memberLastName]
    member.dob = moment.utc(ex[ColumnNames.memberDOB])
    member.gender = gm(ex[ColumnNames.memberGender])

    const sln = ex[ColumnNames.spouseLastName]
    let spouseIsValid = false
    if (sln) {
        const spouse = profile.getSpouse()
        spouse.firstName = ex[ColumnNames.spouseFirstName]
        spouse.lastName = sln
        spouse.dob = moment.utc(ex[ColumnNames.spouseDOB])
        spouse.gender = gm(ex[ColumnNames.spouseGender])
        spouseIsValid = true
    }

    const beginPaymentsAge = moment.duration(65, "years")

    profile.pension.local697 = {
        beginPaymentsAge,
        formOfPayment: spouseIsValid
            ? Local697FormOfPayment.hundredPercentHusbandAndWife
            : Local697FormOfPayment.lifeOnly,
        futureServiceCredits: ex[ColumnNames.futureServiceCredits],
        vestingCredits: ex[ColumnNames.vestingCredits],
        monthlyBen: ex[ColumnNames.monthlyBen],
        l697SavingsBalance: ex[ColumnNames.l697SavingsBalance],
        planPPaymentsAge: beginPaymentsAge.clone(),
        planPEligible: false,
        percentLumpSumElected: 0,
        planPTotalService: 0,
        planPBen: 0,
        planPRate: 0,
        currentYearHours: 0,
        cyCredit: 0,
        pensionExcessHours: ex[ColumnNames.pensionExcessHours],
        planPExcessHours: ex[ColumnNames.planPExcessHours],
        planPCyCredit: ex[ColumnNames.planPCyCredit],

        // added 8/19/2022. Modified 10/6/2023.
        eePreTax: ex[ColumnNames.eePreTax],
        eeAfterTax: ex[ColumnNames.eeAfterTax],
        erContrib: ex[ColumnNames.erContrib],
        rollover: ex[ColumnNames.rollover],
        rothDeferral: ex[ColumnNames.rothDeferral],
        savingsCheck: ex[ColumnNames.savingsCheck],

        futureServiceCredits1Max: ex[ColumnNames.futureServiceCredits1Max],
        IOServicePost2023: ex[ColumnNames.IOServicePost2023],
    }

    profile.pension.IO = {
        beginPaymentsAge,
        formOfPayment: SimplePensionFormOfPayment.singleLifeOnly,
        participatesInIO: ex[ColumnNames.IOEligible] === "Y",
        yearsOfAMembership: moment.duration(
            ex[ColumnNames.IOService] || 0,
            "years"
        ),
    }

    profile.pension.NEBF = {
        beginPaymentsAge,
        formOfPayment: SimplePensionFormOfPayment.singleLifeOnly,
        currentYearsService: moment.duration(
            ex[ColumnNames.NEBFService] || 0,
            "years"
        ),
    }

    loadUpgradeableData(profile)

    return updateLifeExpectancy(member).then(() => {
        if (sln) {
            return updateLifeExpectancy(profile.getSpouse()).then(() => profile)
        }
        return profile
    })
}

function loadMemberRetirementData(profile: Profile) {
    const ex = profile.extraInfo
    do {
        const member = profile.getHeadOfHousehold()
        if (!member) {
            break
        }

        const savingsBal = ex[ColumnNames.l697SavingsBalance]
        if (_.isUndefined(savingsBal)) {
            break
        }

        const p1 = "retirement." + member.id

        // update savings balance for the member
        if (!_.has(profile, p1)) {
            _.set(profile, p1, getDefaultRetirementSavingsPersonInputs())
        }

        const p2 = p1 + ".fixedBalances.retirementSavings"
        // tslint:disable-next-line:no-console
        console.log("Updated profile." + p2 + " to DB value: " + savingsBal)
        _.set(profile, p2, Math.round(savingsBal))
    } while (false)
}

function loadUpgradeableData(profile: Profile) {
    const ex = profile.extraInfo

    // pick up a 401k balance if the user has not entered one
    loadMemberRetirementData(profile)

    profile.pension.shared.hoursWorked = ex[ColumnNames.priorYearsWorked]
    const local697 = profile.pension.local697
    local697.futureServiceCredits = ex[ColumnNames.futureServiceCredits]
    local697.vestingCredits = ex[ColumnNames.vestingCredits]
    local697.monthlyBen = ex[ColumnNames.monthlyBen]
    local697.l697SavingsBalance = ex[ColumnNames.l697SavingsBalance]
    local697.planPEligible = parseYesNo(ex[ColumnNames.planPEligible])
    local697.planPTotalService = ex[ColumnNames.planPTotalService]
    local697.planPBen = ex[ColumnNames.planPBen]
    local697.planPRate = ex[ColumnNames.planPRate]
    local697.currentYearHours = ex[ColumnNames.currentYearHours]
    local697.cyCredit = ex[ColumnNames.cyCredit]
    local697.pensionExcessHours = ex[ColumnNames.pensionExcessHours]
    local697.planPExcessHours = ex[ColumnNames.planPExcessHours]
    local697.planPCyCredit = ex[ColumnNames.planPCyCredit]

    // added 8/29/2022. Modified 10/6/2023
    local697.eePreTax = ex[ColumnNames.eePreTax]
    local697.eeAfterTax = ex[ColumnNames.eeAfterTax]
    local697.erContrib = ex[ColumnNames.erContrib]
    local697.rollover = ex[ColumnNames.rollover]
    local697.rothDeferral = ex[ColumnNames.rothDeferral]
    local697.savingsCheck = ex[ColumnNames.savingsCheck]

    // added 01-25-2023
    local697.futureServiceCredits1Max = ex[ColumnNames.futureServiceCredits1Max]
    local697.IOServicePost2023 = ex[ColumnNames.IOServicePost2023]

    const io = profile.pension.IO
    io.participatesInIO = parseYesNo(ex[ColumnNames.IOEligible])
    io.yearsOfAMembership = moment.duration(
        ex[ColumnNames.IOService] || 0,
        "years"
    )

    const nebf = profile.pension.NEBF
    nebf.currentYearsService = moment.duration(
        ex[ColumnNames.NEBFService] || 0,
        "years"
    )
}

/**
 * Returns an array of the information in 'extraInfo' described by a column name.
 *
 * @param profile - the profile containing the 'extraInfo'
 * @param columnNames - a list of columns required
 */
function pickColumns(profile: Profile, columnNames: string[]): any[] {
    if (!profile.extraInfo) {
        return []
    }
    return _.map(columnNames, (propName) => profile.extraInfo[propName])
}

export { ColumnNames, loadProfilePresets, loadUpgradeableData, pickColumns }



// WEBPACK FOOTER //
// ./src/config/697/ETL/index.ts